import { api } from '@/shared/api/client'
import { url, type QueryValue } from '@/shared/lib/query'
import { dayEndIso, dayStartIso } from '@/shared/lib/format'
import type {
  AuditEntryResponse, AuditExportResponse, AuditFacetsResponse, AuditSummaryResponse,
  CursorPage,
} from '@/shared/api/types'

/** Everything the audit endpoints filter on. The list, summary and export all take the same set. */
export interface AuditFilters {
  action: string[]
  actorId: string[]
  entityType: string[]
  entityId: string
  category: string[]
  outcome: string[]
  /** yyyy-mm-dd in Africa/Nairobi; converted to instants at the day boundary. */
  fromDay: string
  toDay: string
  q: string
  requestId: string
  ip: string
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  action: [], actorId: [], entityType: [], entityId: '',
  category: [], outcome: [], fromDay: '', toDay: '', q: '', requestId: '', ip: '',
}

/**
 * `to` is EXCLUSIVE server-side, so an end date of the 14th must be sent as the
 * start of the 15th — otherwise picking "14th to 14th" returns nothing, which
 * looks like a broken filter rather than an off-by-one.
 */
export function auditParams(f: AuditFilters): Record<string, QueryValue> {
  return {
    action: f.action,
    actorId: f.actorId,
    entityType: f.entityType,
    entityId: f.entityId,
    category: f.category,
    outcome: f.outcome,
    from: f.fromDay ? dayStartIso(f.fromDay) : '',
    to: f.toDay ? dayEndIso(f.toDay) : '',
    q: f.q,
    requestId: f.requestId,
    ip: f.ip,
  }
}

export const listAudit = (f: AuditFilters, cursor?: string, limit = 50) =>
  api.get<CursorPage<AuditEntryResponse>>(
    url('/audit', { ...auditParams(f), cursor, limit }),
  )

export const auditSummary = (f: AuditFilters) =>
  api.get<AuditSummaryResponse>(url('/audit/summary', auditParams(f)))

export const auditFacets = () => api.get<AuditFacetsResponse>('/audit/facets')

export const auditEntry = (id: string) => api.get<AuditEntryResponse>(`/audit/${id}`)

export const auditTrail = (entityType: string, entityId: string, cursor?: string, limit = 50) =>
  api.get<CursorPage<AuditEntryResponse>>(
    url(`/audit/entities/${encodeURIComponent(entityType)}/${entityId}`, { cursor, limit }),
  )

export const exportAudit = (f: AuditFilters) =>
  api.post<AuditExportResponse>(url('/audit/export', auditParams(f)))
